import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit'
import api from '@/lib/api'
import axios from 'axios'

export interface CollaborationDocument {
  id: string
  url: string
  type: string
  name: string
  title: string
  uploadedAt: string
  uploadedBy: {
    id: number
    name: string
  } | null
}

interface CollaborationDocumentsState {
  documentsByCollaboration: Record<string, CollaborationDocument[]>
  loading: Record<string, boolean>
  uploading: Record<string, boolean>
  error: string | null
}

const initialState: CollaborationDocumentsState = {
  documentsByCollaboration: {},
  loading: {},
  uploading: {},
  error: null,
}

// Fetch documents for a collaboration
export const fetchCollaborationDocuments = createAsyncThunk(
  'collaborationDocuments/fetch',
  async (collaborationId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/marketer/collaborations/${collaborationId}/documents`)
      return { collaborationId, documents: response.data.documents }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          (error.response?.data as { error?: string } | undefined)?.error ||
            'Failed to fetch documents',
        )
      }
      return rejectWithValue('Failed to fetch documents')
    }
  },
)

// Upload document to a collaboration
export const uploadCollaborationDocument = createAsyncThunk(
  'collaborationDocuments/upload',
  async (
    {
      collaborationId,
      file,
      title,
      documentType = 'contract',
    }: { collaborationId: string; file: File; title: string; documentType?: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      // 1. Get presigned URL
      const response = await api.post(
        `/marketer/collaborations/${collaborationId}/documents/upload-url`,
        {
          fileName: file.name,
          title,
          documentType,
        },
      )

      const { presignedUrl, publicUrl, documentId } = response.data

      // 2. Upload to S3
      await axios.put(presignedUrl, file, {
        headers: {
          'Content-Type': 'application/pdf',
        },
      })

      // 3. Return new document
      const newDocument: CollaborationDocument = {
        id: documentId,
        url: publicUrl,
        type: documentType,
        name: file.name,
        title,
        uploadedAt: new Date().toISOString(),
        uploadedBy: null, // Will be filled by server on next fetch
      }

      return { collaborationId, document: newDocument }
    } catch (error: unknown) {
      console.error('Upload error:', error)
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          (error.response?.data as { error?: string } | undefined)?.error ||
            'Failed to upload document',
        )
      }
      return rejectWithValue('Failed to upload document')
    }
  },
)

// Delete a document
export const deleteCollaborationDocument = createAsyncThunk(
  'collaborationDocuments/delete',
  async (
    { collaborationId, documentId }: { collaborationId: string; documentId: string },
    { rejectWithValue },
  ) => {
    try {
      await api.delete(`/marketer/collaborations/${collaborationId}/documents/${documentId}`)
      return { collaborationId, documentId }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          (error.response?.data as { error?: string } | undefined)?.error ||
            'Failed to delete document',
        )
      }
      return rejectWithValue('Failed to delete document')
    }
  },
)

const collaborationDocumentsSlice = createSlice({
  name: 'collaborationDocuments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchCollaborationDocuments.pending, (state, action) => {
        state.loading[action.meta.arg] = true
        state.error = null
      })
      .addCase(fetchCollaborationDocuments.fulfilled, (state, action) => {
        const { collaborationId, documents } = action.payload
        state.loading[collaborationId] = false
        state.documentsByCollaboration[collaborationId] = documents
      })
      .addCase(fetchCollaborationDocuments.rejected, (state, action) => {
        state.loading[action.meta.arg] = false
        state.error = action.payload as string
      })
      // Upload
      .addCase(uploadCollaborationDocument.pending, (state, action) => {
        state.uploading[action.meta.arg.collaborationId] = true
      })
      .addCase(uploadCollaborationDocument.fulfilled, (state, action) => {
        const { collaborationId, document } = action.payload
        state.uploading[collaborationId] = false
        if (!state.documentsByCollaboration[collaborationId]) {
          state.documentsByCollaboration[collaborationId] = []
        }
        state.documentsByCollaboration[collaborationId].unshift(document)
      })
      .addCase(uploadCollaborationDocument.rejected, (state, action) => {
        state.uploading[action.meta.arg.collaborationId] = false
        state.error = action.payload as string
      })
      // Delete
      .addCase(deleteCollaborationDocument.fulfilled, (state, action) => {
        const { collaborationId, documentId } = action.payload
        if (state.documentsByCollaboration[collaborationId]) {
          state.documentsByCollaboration[collaborationId] = state.documentsByCollaboration[
            collaborationId
          ].filter((doc) => doc.id !== documentId)
        }
      })
  },
})

export default collaborationDocumentsSlice.reducer

// Base selectors
const selectDocumentsByCollaboration = (state: {
  collaborationDocuments: CollaborationDocumentsState
}) => state.collaborationDocuments.documentsByCollaboration

const selectLoadingState = (state: { collaborationDocuments: CollaborationDocumentsState }) =>
  state.collaborationDocuments.loading

const selectUploadingState = (state: { collaborationDocuments: CollaborationDocumentsState }) =>
  state.collaborationDocuments.uploading

// Create a stable empty array to avoid creating new references
const EMPTY_DOCUMENTS_ARRAY: CollaborationDocument[] = []

// Memoized selectors
export const selectCollaborationDocuments = createSelector(
  [
    selectDocumentsByCollaboration,
    (_state: { collaborationDocuments: CollaborationDocumentsState }, collaborationId: string) =>
      collaborationId,
  ],
  (documentsByCollaboration, collaborationId) =>
    documentsByCollaboration[collaborationId] || EMPTY_DOCUMENTS_ARRAY,
)

export const selectDocumentsLoading = createSelector(
  [
    selectLoadingState,
    (_state: { collaborationDocuments: CollaborationDocumentsState }, collaborationId: string) =>
      collaborationId,
  ],
  (loading, collaborationId) => loading[collaborationId] || false,
)

export const selectDocumentsUploading = createSelector(
  [
    selectUploadingState,
    (_state: { collaborationDocuments: CollaborationDocumentsState }, collaborationId: string) =>
      collaborationId,
  ],
  (uploading, collaborationId) => uploading[collaborationId] || false,
)
